import { FILTER_VOCABULARY } from '@forwardflow/features/campaign-flow/data/mock'
import { EVENT_OPTIONS } from '@forwardflow/features/campaign-filter/constants'
import {
   IActivityTrigger,
   IAudience,
   IFilterRow,
   IInterpretation,
   TCampaignKind,
   TLeadType,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'

/**
 * "Describe your leads" → the dashboard's own campaign filter: kinds of
 * leads, buy / sell / rent, filter rows (source, stage, tags, pond, inquired
 * property, include or exclude) and activity triggers.
 *
 * Phrases are matched against the organization's real Follow Up Boss values
 * (`setFilterVocabulary`), tolerant of plurals, ".com", punctuation and small
 * typos. Words like "stage", "tag" or "pond" next to a value decide where it
 * goes; "not", "except", "without" exclude it. Nothing is assumed: what the
 * text does not mention stays unchosen, and words that matched nothing are
 * reported back.
 */

export const KIND_LABEL: Record<TCampaignKind, string> = {
   'new-lead': 'New leads',
   revive: 'Cold leads',
   activity: 'Recent activity',
}
export const LEAD_TYPE_LABEL: Record<TLeadType, string> = {
   buyer: 'Buyers',
   seller: 'Sellers',
   rental: 'Renters',
}
export const KINDS: TCampaignKind[] = ['new-lead', 'revive', 'activity']
export const LEAD_TYPES: TLeadType[] = ['buyer', 'seller', 'rental']

/** The lead types a campaign speaks to: the chosen ones, or every type when "all leads". */
export const audienceTypes = (a: Pick<IAudience, 'leadTypes'>): TLeadType[] =>
   a.leadTypes.length ? a.leadTypes : LEAD_TYPES

export const kindLabel = (k: TCampaignKind) => KIND_LABEL[k]
export const leadTypeLabel = (t: TLeadType) => LEAD_TYPE_LABEL[t]

export type TFilterVocabulary = Record<
   IFilterRow['field'],
   { value: string; label: string }[]
>

/**
 * The values filters can take. Starts as the demo account; the live service
 * replaces it with the organization's Follow Up Boss sources, stages, tags
 * and ponds (`setFilterVocabulary`), so parsing and labels use real values.
 */
let vocabulary: TFilterVocabulary = FILTER_VOCABULARY
export const setFilterVocabulary = (v: TFilterVocabulary) => {
   vocabulary = v
}
export const filterVocabularyNow = () => vocabulary

/* ---------- Words ---------- */

const singular = (w: string) =>
   w.length > 4 && /ies$/.test(w)
      ? `${w.slice(0, -3)}y`
      : w.length > 3 && /[^s]s$/.test(w)
        ? w.slice(0, -1)
        : w

/** "Realtor.com Buyers!" → ["realtor", "buyer"]. */
export const words = (text: string) =>
   text
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/\.com\b/g, '')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean)
      .map(singular)

const distance = (a: string, b: string) => {
   if (Math.abs(a.length - b.length) > 2) return 3
   const row = Array.from({ length: b.length + 1 }, (_, i) => i)
   for (let i = 1; i <= a.length; i += 1) {
      let prev = row[0]
      row[0] = i
      for (let j = 1; j <= b.length; j += 1) {
         const cur = row[j]
         row[j] = Math.min(
            row[j] + 1,
            row[j - 1] + 1,
            prev + (a[i - 1] === b[j - 1] ? 0 : 1)
         )
         prev = cur
      }
   }
   return row[b.length]
}

/** Same word, allowing one typo in words of 5+ letters and two in 9+. */
const sameWord = (a: string, b: string) =>
   a === b ||
   (a.length >= 5 && b.length >= 5 && distance(a, b) <= (a.length >= 9 ? 2 : 1))

/* ---------- What the words mean ---------- */

const KIND_PATTERNS: Array<[TCampaignKind, RegExp]> = [
   ['new-lead', /\b(new|fresh|just came in|came in|incoming|brand new)\b/],
   [
      'revive',
      /\b(cold|old|older|stale|revive|reviv\w*|re engage|dormant|went quiet|gone quiet|never (responded|replied|answered)|havent (responded|replied)|aged|past leads?)\b/,
   ],
   [
      'activity',
      /\b(viewed|saved|visited|searched|clicked|browsing|activity|active on|looked at|open house)\b/,
   ],
]
const TYPE_PATTERNS: Array<[TLeadType, RegExp]> = [
   ['buyer', /\b(buy|buyer|buyers|buying|purchas\w*|home shoppers?)\b/],
   [
      'seller',
      /\b(sell|seller|sellers|selling|listing|listings|home value|valuation|cma)\b/,
   ],
   [
      'rental',
      /\b(rent|renter|renters|renting|rental|rentals|tenant|tenants|lease)\b/,
   ],
]
const ALL_TYPES = /\b(everyone|all leads|any lead|every lead|all my leads)\b/

/** Activity phrases → Follow Up Boss event names (EVENT_OPTIONS). */
const EVENT_PATTERNS: Array<[string, RegExp]> = [
   ['Saved Property Search', /saved (a |their )?search/],
   [
      'Saved Property',
      /saved (a |the )?(home|house|propert|listing)|\bsaved\b(?! search)/,
   ],
   [
      'Viewed Property',
      /viewed (a |the )?(home|house|propert|listing)|\bviewed\b/,
   ],
   ['Visited Website', /visited (the |our |my )?(site|website)/],
   ['Property Search', /\bsearched\b|property search/],
   ['Visited Open House', /open house/],
   ['Seller Inquiry', /seller inquir/],
   ['Property Inquiry', /property inquir/],
   ['General Inquiry', /general inquir/],
]

const FIELD_HINT: Record<string, IFilterRow['field']> = {
   stage: 'stage',
   stages: 'stage',
   tag: 'tags',
   tags: 'tags',
   tagged: 'tags',
   pond: 'assigned_pond_id',
   ponds: 'assigned_pond_id',
   source: 'source',
   from: 'source',
   via: 'source',
}
const NEGATIONS = new Set([
   'not',
   'except',
   'excluding',
   'exclude',
   'without',
   'no',
   'minus',
   'but',
])
/** Values these single words would match are only taken with a hint ("stage cold", "tagged buyer"). */
const AMBIGUOUS = new Set([
   'buyer',
   'seller',
   'renter',
   'new',
   'cold',
   'old',
   'lead',
   'hot',
   'active',
   'past',
   'client',
])
const FILLER = new Set(
   'a an and or the my our your me us i we who that which with in on of asked ask asking about inquired interested looking want wanted to for at by is are were was be been call text reach out contact everyone all any every lead people person them they their just only also than more less day days week weeks month months year years ago last past this from via stage tag tagged pond source not except excluding exclude without no but minus please want would like get some have has had into there here it its these those'.split(
      ' '
   )
)

const FIELD_LABEL: Record<IFilterRow['field'], string> = {
   source: 'Source',
   stage: 'Stage',
   tags: 'Tag',
   assigned_pond_id: 'Pond',
   inquired_property: 'Asked about',
}

interface IHit {
   field: IFilterRow['field']
   value: string
   label: string
   start: number
   end: number
}

/** Every vocabulary value whose words appear in the text, longest first, without overlaps. */
const findValues = (tokens: string[]): IHit[] => {
   const hits: IHit[] = []
   ;(Object.keys(vocabulary) as IFilterRow['field'][]).forEach((field) =>
      vocabulary[field].forEach((o) => {
         const want = words(o.label)
         if (!want.length) return
         for (let i = 0; i + want.length <= tokens.length; i += 1) {
            if (want.every((w, k) => sameWord(tokens[i + k], w))) {
               hits.push({
                  field,
                  value: o.value,
                  label: o.label,
                  start: i,
                  end: i + want.length,
               })
            }
         }
      })
   )
   hits.sort((a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start)
   const taken = new Set<number>()
   return hits.filter((h) => {
      for (let i = h.start; i < h.end; i += 1) if (taken.has(i)) return false
      for (let i = h.start; i < h.end; i += 1) taken.add(i)
      return true
   })
}

/** Hint words that can also follow a value: "Hot stage", "Buyer pond". */
const AFTER_HINTS = new Set(['stage', 'stages', 'pond', 'ponds', 'tag', 'tags'])

/**
 * Where the text says a value belongs: its own words ("Seller Pond"), a hint
 * right before it ("stage nurture", "tagged investor", "from Zillow") or a
 * noun right after it ("Hot stage").
 */
const hintNear = (tokens: string[], h: IHit) => {
   const own = words(h.label)
      .map((w) => FIELD_HINT[w])
      .find((f) => f === h.field)
   if (own) return own
   const before = [h.start - 1, h.start - 2]
      .filter((i) => i >= 0)
      .map((i) => FIELD_HINT[tokens[i]])
      .find(Boolean)
   if (before) return before
   const next = tokens[h.end]
   return next && AFTER_HINTS.has(next) ? FIELD_HINT[next] : undefined
}

const negated = (tokens: string[], at: number) => {
   for (let i = at - 1; i >= Math.max(0, at - 3); i -= 1) {
      if (NEGATIONS.has(tokens[i])) return true
      if (tokens[i] === 'and' || tokens[i] === 'or') return false
   }
   return false
}

const numberBefore = (s: string, unit: RegExp) => {
   const m = s.match(new RegExp(`(\\d+)\\s*(${unit.source})`))
   if (!m) return null
   const n = Number(m[1])
   return /week/.test(m[2]) ? n * 7 : /month/.test(m[2]) ? n * 30 : n
}

export function interpretDescription(text: string): IInterpretation {
   const s = ` ${words(text).join(' ')} `
   const raw = ` ${text.toLowerCase()} `
   const tokens = words(text)
   const used = new Set<number>()
   const understood: string[] = []

   /*
    * One campaign type per campaign. Exactly one mentioned = chosen; none or
    * several = left for the screen to ask ("new and cold" become two campaigns).
    */
   const said = [
      ...new Set(KIND_PATTERNS.filter(([, re]) => re.test(s)).map(([k]) => k)),
   ]
   if (
      /(older|more) than \d+|over \d+ (day|week|month)/.test(raw) &&
      !said.includes('revive')
   )
      said.push('revive')
   if (
      /(last|past) \d+ (day|week)/.test(raw) &&
      !said.includes('new-lead') &&
      !said.includes('activity')
   )
      said.push('new-lead')
   const kindsMentioned: TCampaignKind[] = said
   const kinds: TCampaignKind[] = said.length === 1 ? [said[0]] : []

   let leadTypes = TYPE_PATTERNS.filter(([, re]) => re.test(raw)).map(
      ([t]) => t
   )
   /* "All leads" = no lead-type filter. */
   if (ALL_TYPES.test(raw)) leadTypes = []

   const filters: IFilterRow[] = []
   const add = (
      field: IFilterRow['field'],
      exclude: boolean,
      value: string
   ) => {
      const operator = exclude ? 'NOT_CONTAINS_ANY' : 'CONTAINS_ANY'
      const row = filters.find(
         (f) => f.field === field && f.operator === operator
      )
      if (row) {
         if (!row.value.includes(value)) row.value.push(value)
      } else filters.push({ field, operator, logic: 'AND', value: [value] })
   }

   findValues(tokens).forEach((h) => {
      const hint = hintNear(tokens, h)
      const single = h.end - h.start === 1
      if (single && AMBIGUOUS.has(tokens[h.start]) && hint !== h.field) return
      if (hint && hint !== h.field && hint !== 'source') return
      const exclude = negated(tokens, h.start)
      add(h.field, exclude, h.value)
      for (let i = h.start; i < h.end; i += 1) used.add(i)
      understood.push(
         `${exclude ? 'Not ' : ''}${exclude ? FIELD_LABEL[h.field].toLowerCase() : FIELD_LABEL[h.field]}: ${h.label}`
      )
   })

   let activity: IActivityTrigger | undefined
   if (kindsMentioned.includes('activity')) {
      const events = EVENT_PATTERNS.filter(([, re]) => re.test(raw))
         .map(([e]) => e)
         .filter((e) => EVENT_OPTIONS.some((o) => o.value === e))
      activity = {
         events: events.length ? [...new Set(events)] : ['all'],
         lookbackDays: numberBefore(raw, /days?|weeks?|months?/),
         pondIds: filters
            .filter(
               (f) =>
                  f.field === 'assigned_pond_id' &&
                  f.operator === 'CONTAINS_ANY'
            )
            .flatMap((f) => f.value.map(String)),
      }
      if (events.length)
         understood.push(`When they: ${events.join(', ').toLowerCase()}`)
   }

   /* What the patterns used, so it is not reported as unknown. */
   const known = new RegExp(
      [...KIND_PATTERNS, ...TYPE_PATTERNS]
         .map(([, re]) => re.source)
         .join('|') +
         '|\\d+|home|house|property|propert|website|site|inquir\\w*|days?|weeks?|months?'
   )
   const unknown = tokens.filter(
      (w, i) =>
         !used.has(i) && !FILLER.has(w) && !known.test(` ${w} `) && w.length > 2
   )

   /* Read back in the order of the screen: kinds, lead types, then filters. */
   understood.unshift(
      ...(kindsMentioned.length === 1
         ? [KIND_LABEL[kindsMentioned[0]]]
         : kindsMentioned.map((k) => `${KIND_LABEL[k]} (pick one)`)),
      ...(leadTypes.length
         ? leadTypes.map((t) => LEAD_TYPE_LABEL[t])
         : ALL_TYPES.test(raw)
           ? ['All leads']
           : [])
   )

   const audience = {
      kinds,
      leadTypes,
      filters,
      ...(activity ? { activity } : {}),
   }
   return {
      description: text.trim(),
      name: autoName(audience),
      ...audience,
      understood,
      unknown: [...new Set(unknown)],
      kindsMentioned,
   }
}

/* ---------- The name ---------- */

const KIND_WORD: Record<TCampaignKind, string> = {
   'new-lead': 'New',
   revive: 'Cold',
   activity: 'Active',
}

const listOf = (xs: string[], max = 2) =>
   xs.length <= max
      ? xs.join(' & ')
      : `${xs.slice(0, max).join(', ')} +${xs.length - max}`

/**
 * A name people recognise in a list, built from what was chosen:
 * "New Zillow buyers", "Cold sellers · Hot stage", "Active buyers · Saved property",
 * "New buyers and sellers · not Facebook". Empty until something is chosen.
 */
export const autoName = (
   a: Pick<IAudience, 'kinds' | 'leadTypes' | 'filters' | 'activity'>
): string => {
   const inc = (field: IFilterRow['field']) =>
      a.filters
         .filter((f) => f.field === field && f.operator !== 'NOT_CONTAINS_ANY')
         .flatMap((f) => f.value.map((v) => valueLabel(field, v)))
   const exc = a.filters
      .filter((f) => f.operator === 'NOT_CONTAINS_ANY')
      .flatMap((f) => f.value.map((v) => valueLabel(f.field, v)))
   const sources = inc('source')
   if (
      !a.kinds.length &&
      !a.leadTypes.length &&
      !a.filters.some((f) => f.value.length)
   )
      return ''

   const kind = a.kinds.map((k) => KIND_WORD[k])
   const kindWord = kind.length
      ? kind.length === 1
         ? kind[0]
         : `${kind.slice(0, -1).join(', ')} and ${kind.at(-1)?.toLowerCase()}`
      : ''
   const who = a.leadTypes.map((t) => LEAD_TYPE_LABEL[t].toLowerCase())
   const people = who.length
      ? who.length > 1
         ? `${who.slice(0, -1).join(', ')} and ${who.at(-1)}`
         : who[0]
      : 'leads'
   const head = [kindWord, sources.length ? listOf(sources) : '', people]
      .filter(Boolean)
      .join(' ')

   const extra: string[] = []
   const ponds = inc('assigned_pond_id')
   const stages = inc('stage')
   const tags = inc('tags')
   const homes = inc('inquired_property')
   if (ponds.length) extra.push(listOf(ponds))
   if (stages.length) extra.push(`${listOf(stages)} stage`)
   if (tags.length) extra.push(`#${listOf(tags)}`)
   if (homes.length) extra.push(`asked about ${listOf(homes, 1)}`)
   const events = (a.activity?.events || []).filter((e) => e !== 'all')
   if (a.kinds.includes('activity') && events.length)
      extra.push(listOf(events, 1).toLowerCase())
   if (exc.length) extra.push(`not ${listOf(exc)}`)

   const n = [head, ...extra.slice(0, 2)].join(' · ')
   return n.charAt(0).toUpperCase() + n.slice(1)
}

/** One line for summaries: "New and cold leads · Buyers, Sellers". */
export const audienceSummary = (a: Pick<IAudience, 'kinds' | 'leadTypes'>) =>
   `${a.kinds.map((k) => KIND_LABEL[k]).join(' + ')} · ${a.leadTypes.map((t) => LEAD_TYPE_LABEL[t]).join(', ')}`

/** Nothing preselected: the user chooses kinds and lead types. */
export const defaultAudience = (): Pick<
   IAudience,
   'kinds' | 'leadTypes' | 'filters'
> => ({ kinds: [], leadTypes: [], filters: [] })

/** Label for a filter value (ponds store ids). */
export const valueLabel = (field: IFilterRow['field'], v: string | number) =>
   vocabulary[field]?.find((o) => o.value === String(v))?.label ?? String(v)
